'use client';
import { useState, useRef } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import Link from 'next/link';
import { ArrowLeft, Download, Plus, Trash2, Upload, FileUp } from 'lucide-react';

export default function CsvEditorPage() {
  const [csvInput, setCsvInput] = useState('');
  const [tableData, setTableData] = useState<string[][]>([]);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadCsv = () => {
    try {
      setError('');
      if (!csvInput.trim()) {
        setError('Please enter CSV data');
        return;
      }

      const lines = csvInput.trim().split('\n');
      const data = lines.map(line => line.split(',').map(cell => cell.trim()));
      setTableData(data);
      setIsEditing(true);
    } catch (err) {
      setError('Error loading CSV data.');
    }
  };

  const updateCell = (rowIndex: number, colIndex: number, value: string) => {
    const newData = [...tableData];
    newData[rowIndex][colIndex] = value;
    setTableData(newData);
  };

  const addRow = () => {
    if (tableData.length === 0) return;
    const newRow = new Array(tableData[0].length).fill('');
    setTableData([...tableData, newRow]);
  };

  const deleteRow = (rowIndex: number) => {
    const newData = tableData.filter((_, index) => index !== rowIndex);
    setTableData(newData);
  };

  const addColumn = () => {
    const newData = tableData.map(row => [...row, '']);
    setTableData(newData);
  };

  const deleteColumn = (colIndex: number) => {
    const newData = tableData.map(row => row.filter((_, index) => index !== colIndex));
    setTableData(newData);
  };

  const downloadCsv = () => {
    const csvContent = tableData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'edited.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetEditor = () => {
    setTableData([]);
    setIsEditing(false);
    setCsvInput('');
  };

  const handleFileUpload = (file: File) => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      setError('Please upload a CSV or TXT file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setCsvInput(text);
    };
    reader.onerror = () => {
      setError('Error reading file');
    };
    reader.readAsText(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="marketing-theme tool-page">

      <Navbar />

      <main className="tool-page-main flex flex-col items-center">
        <div className="w-full max-w-6xl">
          <Link 
            href="/tools" 
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              CSV Editor Online
            </h1>
            <p className="text-lg text-muted-foreground">
              Edit your CSV data in an interactive table
            </p>
          </div>

          {/* Standalone CSV Editor Download Section */}
          <div className="mb-8 p-6 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 rounded-xl border-2 border-blue-200 dark:border-blue-800">
            <div className="flex flex-col lg:flex-row gap-6 items-center">
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-3 flex items-center gap-2">
                  <Download className="w-6 h-6 text-blue-600" />
                  Standalone CSV Editor (HTML)
                </h2>
                <p className="text-muted-foreground mb-4">
                  Download our beautiful macOS-style CSV editor as a single HTML file. Works offline, no dependencies, and fully responsive!
                </p>
                
                <a
                  href="/csv-editor.html"
                  download="csv-editor.html"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors shadow-lg hover:shadow-xl"
                >
                  <Download className="w-5 h-5" />
                  Download CSV Editor (HTML)
                </a>
              </div>
              <div className="flex-1 max-w-md">
                <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <img 
                    src="/csv-editor-html-screenshot.png" 
                    alt="CSV Editor Preview - macOS style interface"
                    className="w-full max-h-48 object-contain"
                  />
                </div>
              </div>
            </div>
          </div>

          {!isEditing ? (
            <div className="space-y-6">
              {/* File Upload Area */}
              <div>
                <label className="text-sm font-medium mb-2 block">Upload or Drop CSV File</label>
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={triggerFileInput}
                  className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all ${
                    isDragging 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-gray-300 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-600'
                  }`}
                >
                  <FileUp className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
                  <p className="text-lg font-medium mb-2">
                    {isDragging ? 'Drop your file here' : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Supports .csv and .txt files
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                </div>
              </div>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-background text-muted-foreground">OR</span>
                </div>
              </div>

              {/* Text Input Area */}
              <div>
                <label className="text-sm font-medium mb-2 block">Paste your CSV data</label>
                <Textarea
                  placeholder="name,age,city&#10;John,30,New York&#10;Jane,25,London&#10;Bob,35,Paris"
                  value={csvInput}
                  onChange={(e) => setCsvInput(e.target.value)}
                  className="min-h-[200px] font-mono text-sm"
                />
              </div>
              <Button 
                onClick={loadCsv} 
                className="w-full"
                size="lg"
              >
                <Upload className="w-4 h-4 mr-2" />
                Load CSV for Editing
              </Button>
              {error && (
                <div className="text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  {error}
                </div>
              )}

              {/* Example */}
              <div className="mt-8 p-6 bg-card rounded-lg border">
                <h3 className="font-semibold mb-2">Example CSV:</h3>
                <pre className="text-sm bg-muted p-4 rounded overflow-x-auto">
                  name,age,city,country{'\n'}
                  John,30,New York,USA{'\n'}
                  Jane,25,London,UK{'\n'}
                  Bob,35,Paris,France
                </pre>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                <Button onClick={addRow} variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Row
                </Button>
                <Button onClick={addColumn} variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Column
                </Button>
                <Button onClick={downloadCsv} variant="default" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV
                </Button>
                <Button onClick={resetEditor} variant="outline" size="sm">
                  Reset
                </Button>
              </div>

              {/* Editable Table */}
              <div className="overflow-x-auto border rounded-lg bg-card">
                <table className="w-full">
                  <thead>
                    <tr className="bg-muted">
                      <th className="w-12 p-2 text-center border-r">#</th>
                      {tableData[0]?.map((_, colIndex) => (
                        <th key={colIndex} className="p-2 border-r relative group">
                          <div className="flex items-center justify-between">
                            <span className="flex-1">Column {colIndex + 1}</span>
                            <button
                              onClick={() => deleteColumn(colIndex)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700"
                              title="Delete column"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((row, rowIndex) => (
                      <tr key={rowIndex} className="border-t hover:bg-muted/50 group">
                        <td className="p-2 text-center border-r bg-muted/30 font-mono text-sm">
                          <div className="flex items-center justify-center gap-1">
                            {rowIndex + 1}
                            <button
                              onClick={() => deleteRow(rowIndex)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700"
                              title="Delete row"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                        {row.map((cell, colIndex) => (
                          <td key={colIndex} className="p-0 border-r">
                            <Input
                              value={cell}
                              onChange={(e) => updateCell(rowIndex, colIndex, e.target.value)}
                              className="border-0 rounded-none focus-visible:ring-1 focus-visible:ring-blue-500"
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="text-sm text-muted-foreground bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                💡 Tip: Click on any cell to edit. Hover over row/column numbers to see delete options.
              </div>
            </div>
          )}
        </div>
      </main>

      {/* User Problems & FAQs for SEO */}
      <section className="max-w-4xl mx-auto mt-16 mb-12 p-6 bg-card rounded-lg border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold mb-4">Common CSV Editor Questions & Problems</h2>
        <div className="space-y-6">
          <div>
            <p className="font-semibold">1. How do I view a CSV file online?</p>
            <p className="text-muted-foreground">Simply upload or paste your CSV data above. The table will display your data for easy viewing and editing.</p>
          </div>
          <div>
            <p className="font-semibold">2. Can I edit CSV files directly in the browser?</p>
            <p className="text-muted-foreground">Yes, you can edit any cell in the table. Add or remove rows and columns as needed, then download your updated CSV.</p>
          </div>
          <div>
            <p className="font-semibold">3. How do I save my edited CSV?</p>
            <p className="text-muted-foreground">After editing, click the "Download CSV" button to save your changes as a new CSV file.</p>
          </div>
          <div>
            <p className="font-semibold">4. Is my CSV data secure?</p>
            <p className="text-muted-foreground">All processing happens in your browser. Your CSV data is never uploaded to any server.</p>
          </div>
          <div>
            <p className="font-semibold">5. Can I import CSV from Excel?</p>
            <p className="text-muted-foreground">Yes, export your Excel sheet as CSV, then upload or paste it here to view and edit.</p>
          </div>
          <div>
            <p className="font-semibold">6. How do I fix CSV formatting errors?</p>
            <p className="text-muted-foreground">Check for missing commas or inconsistent columns. The editor will show errors if the CSV is not valid.</p>
          </div>
          <div>
            <p className="font-semibold">7. Can I add or delete columns and rows?</p>
            <p className="text-muted-foreground">Use the "Add Row", "Add Column", and delete buttons to modify your CSV structure easily.</p>
          </div>
          <div>
            <p className="font-semibold">8. What file types are supported?</p>
            <p className="text-muted-foreground">You can upload .csv and .txt files. For other formats, convert them to CSV first.</p>
          </div>
          <div>
            <p className="font-semibold">9. How do I handle large CSV files?</p>
            <p className="text-muted-foreground">For very large files, performance may vary. Try splitting large files or use desktop tools for heavy editing.</p>
          </div>
          <div>
            <p className="font-semibold">10. Can I use this CSV editor for free?</p>
            <p className="text-muted-foreground">Yes, this online CSV editor is completely free to use with no registration required.</p>
          </div>
        </div>
      </section>
      <div className="text-center text-xs text-muted-foreground pb-4">online</div>
      <Footer />
    </div>
  );
}
